"use client";

import React, { useState, useEffect } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { RadioGroup, RadioGroupItem } from '@vaiu/ui/radio-group';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { i18n } from '@/utils/i18n';
import GoogleSignIn from '@/components/GoogleSignIn';
import PhoneSignup from '@/components/PhoneSignup';
import { useAuth } from '@/context/AuthContext';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';
import { 
  CheckCircle, 
  User, 
  Store, 
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  Smartphone
} from 'lucide-react';
import attributionService, { AttributionData } from '@/services/attributionService';
import { platformApi } from '@/services/platformApi';

type UserType = 'person' | 'seller';

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  userType: UserType;
}

const SignupWithType = () => {
  const { isGuest } = useAuth();
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'person',
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [attributionData, setAttributionData] = useState<AttributionData | null>(null);
  const [showPhoneSignup, setShowPhoneSignup] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();

  // Check if coming from guest state
  const fromGuest = location.state?.fromGuest || false;
  const guestUsername = location.state?.guestUsername;

  // Capture attribution data on page load
  useEffect(() => {
    const attribution = attributionService.captureAttribution();
    setAttributionData(attribution);
    
    const from = searchParams.get('from');
    const ref = searchParams.get('ref');
    const campaign = searchParams.get('campaign');
    
    if (from || ref || campaign) {
      const customData: Record<string, any> = {};
      if (from) customData.source_page = from;
      if (ref) customData.referral_code = ref;
      if (campaign) customData.campaign_override = campaign;
      
      attributionService.updateAttribution(customData);
      setAttributionData(prev => prev ? { ...prev, custom_data: { ...prev.custom_data, ...customData } } : null);
    }
  }, [searchParams]);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && user && !fromGuest) {
      console.log('🔄 User is already authenticated, redirecting to dashboard');
      navigate(getDefaultAuthenticatedDestination());
    }
  }, [user, authLoading, navigate, fromGuest]);

  const calculatePasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,  // Just for strength indicator, not enforced
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[^A-Za-z0-9]/.test(password)
    };
    
    Object.values(checks).forEach(check => {
      if (check) score++;
    });
    
    // Determine strength level (length is just one factor, not required)
    if (score === 0 && password.length > 0) {
      return { score, label: i18n.__('signup.passwordStrength.veryWeak'), color: 'text-red-600', bgColor: 'bg-red-500' };
    } else if (score <= 2) {
      return { score, label: i18n.__('signup.passwordStrength.weak'), color: 'text-red-500', bgColor: 'bg-red-400' };
    } else if (score === 3) {
      return { score, label: i18n.__('signup.passwordStrength.medium'), color: 'text-orange-500', bgColor: 'bg-orange-400' };
    } else if (score === 4) {
      return { score, label: i18n.__('signup.passwordStrength.strong'), color: 'text-green-500', bgColor: 'bg-green-400' };
    } else {
      return { score, label: i18n.__('signup.passwordStrength.veryStrong'), color: 'text-green-600', bgColor: 'bg-green-500' };
    }
  };

  const validateForm = () => {
    const errors = {
      email: '',
      password: '',
      confirmPassword: '',
    };

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      errors.email = i18n.__('signup.errors.emailRequired');
    } else if (!emailRegex.test(formData.email)) {
      errors.email = i18n.__('signup.errors.invalidEmail');
    }

    // Password validation
    if (!formData.password) {
      errors.password = i18n.__('signup.errors.passwordRequired');
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = i18n.__('signup.errors.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = i18n.__('signup.errors.passwordsMismatch');
    }

    setFieldErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Prepare registration data
      const registrationData: any = {
        email: formData.email,
        password: formData.password,
        attribution: attributionData,
        userType: formData.userType
      };

      // If user is currently a guest, include guest token to preserve UUID and data
      if (isGuest) {
        const guestToken = platformApi.getToken();
        if (guestToken) {
          registrationData.guestToken = guestToken;
        }
      }

      // Register the user
      const response = await platformApi.register(registrationData);

      // Track successful signup
      attributionService.trackConversion('signup_completed', {
        email: formData.email,
        signup_method: 'email',
        user_type: formData.userType
      });
      
      attributionService.clearAttribution();
      
      setSuccess(i18n.__('signup.success.registration'));
      
      // Auto-login after successful registration
      setTimeout(async () => {
        try {
          const loginResult = await platformApi.login(formData.email, formData.password);
          
          if (loginResult.token) {
            await login(loginResult.token);
            
            // Add MERCHANT role for sellers (fallback if backend didn't add it)
            if (formData.userType === 'seller') {
              try {
                await platformApi.profile.addUserRole('MERCHANT');
                console.log('MERCHANT role added successfully');
                
                // Refresh the user profile to get updated roles
                await platformApi.profile.getProfile();
              } catch (roleError) {
                console.error('Failed to add MERCHANT role:', roleError);
                // Continue anyway - user can still proceed with onboarding
              }
            }
            
            // Navigate based on user type
            if (formData.userType === 'seller') {
              navigate('/merchant-onboarding');
            } else {
              navigate(getDefaultAuthenticatedDestination());
            }
          }
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
          navigate('/login?newUser=true');
        }
      }, 1500);

    } catch (err: any) {
      const errorMessage = err.message || i18n.__('signup.errors.registrationFailed');
      
      // Track failed signup
      attributionService.trackConversion('signup_failed', {
        email: formData.email,
        signup_method: 'email',
        user_type: formData.userType,
        error: errorMessage
      });
      
      // Handle specific errors
      let friendlyError = errorMessage;
      if (errorMessage.toLowerCase().includes('email already')) {
        friendlyError = i18n.__('signup.errors.emailTaken');
      }
      
      setError(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignInSuccess = async (token: string) => {
    try {
      setLoading(true);
      setError('');
      
      attributionService.trackConversion('signup_completed', {
        signup_method: 'google',
        user_type: formData.userType
      });
      
      attributionService.clearAttribution();
      
      localStorage.setItem('authToken', token);
      setSuccess(i18n.__('signup.success.google'));
      
      setTimeout(() => {
        if (formData.userType === 'seller') {
          window.location.href = '/merchant-onboarding';
        } else {
          window.location.href = '/dashboard';
        }
      }, 1000);
      
    } catch (err: any) {
      console.error('Google Sign-In error:', err);
      setError(i18n.__('signup.errors.googleSignInFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSignupSuccess = async (_token: string) => {
    attributionService.trackConversion('signup_completed', {
      signup_method: 'phone',
      user_type: formData.userType,
    });
    attributionService.clearAttribution();

    if (formData.userType === 'seller') {
      navigate('/merchant-onboarding', { replace: true });
    } else {
      navigate(getDefaultAuthenticatedDestination(), { replace: true });
    }
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  if (showPhoneSignup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <PhoneSignup
          onSuccess={handlePhoneSignupSuccess}
          onBack={() => setShowPhoneSignup(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className={`space-y-1 ${fromGuest ? 'pb-4' : 'pb-2'}`}>
            <CardTitle className="text-2xl font-bold text-center">
              {fromGuest ? i18n.__('signup.upgradeGuest') : i18n.__('signup.createAccount')}
            </CardTitle>
            {fromGuest && (
              <CardDescription className="text-center">
                {i18n.__('signup.convertGuest', { guestUsername })}
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{i18n.__('signup.accountType')}</Label>
              <RadioGroup 
                value={formData.userType} 
                onValueChange={(value: UserType) => setFormData(prev => ({ ...prev, userType: value }))}
                className="grid grid-cols-2 gap-2"
              >
                <Label
                  htmlFor="person"
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 cursor-pointer transition-all touch-manipulation ${
                    formData.userType === 'person' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, userType: 'person' }))}
                >
                  <RadioGroupItem value="person" id="person" className="sr-only" />
                  <User className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">{i18n.__('signup.individual')}</span>
                </Label>

                <Label
                  htmlFor="seller"
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 cursor-pointer transition-all touch-manipulation ${
                    formData.userType === 'seller' 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, userType: 'seller' }))}
                >
                  <RadioGroupItem value="seller" id="seller" className="sr-only" />
                  <Store className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">{i18n.__('signup.merchant')}</span>
                </Label>
              </RadioGroup>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}

            {/* Preferred signup: phone, then Google */}
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12"
                onClick={() => setShowPhoneSignup(true)}
                disabled={loading}
              >
                <Smartphone className="w-4 h-4 mr-2" />
                {i18n.__('login.with_phone') || 'Sign up with Phone Number'}
              </Button>

              <GoogleSignIn
                onSuccess={handleGoogleSignInSuccess}
                onError={(googleError) => setError(googleError)}
                disabled={loading}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">
                  {i18n.__('signup.orContinueWith') || 'Or sign up with email'}
                </span>
              </div>
            </div>

            {/* Email + password signup (last) */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="email">{i18n.__('signup.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={i18n.__('signup.emailPlaceholder')}
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={fieldErrors.email ? 'border-red-500' : ''}
                  disabled={loading}
                />
                {fieldErrors.email && (
                  <p className="text-sm text-red-500">{fieldErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{i18n.__('signup.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={i18n.__('signup.passwordPlaceholder')}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className={fieldErrors.password ? 'border-red-500' : ''}
                    disabled={loading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {fieldErrors.password && (
                  <p className="text-sm text-red-500">{fieldErrors.password}</p>
                )}
                
                {formData.password && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${passwordStrength.bgColor}`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${passwordStrength.color}`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{i18n.__('signup.confirmPassword')}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={i18n.__('signup.confirmPasswordPlaceholder')}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className={fieldErrors.confirmPassword ? 'border-red-500' : ''}
                    disabled={loading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="text-sm text-red-500">{fieldErrors.confirmPassword}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold" 
                disabled={loading}
                size="lg"
              >
                {loading ? (
                  i18n.__('signup.creatingAccount')
                ) : (
                  <>
                    {formData.userType === 'person' ? i18n.__('signup.createPersonal') : i18n.__('signup.createMerchant')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center text-sm">
              {i18n.__('signup.haveAccount')}{' '}
              <Link to="/login" className="text-blue-600 hover:underline">
                {i18n.__('signup.signIn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignupWithType;
